import { computed, onScopeDispose, ref } from 'vue'
import { defineStore } from 'pinia'

import { authApi } from '@/api/auth'
import { advanceApiSessionBoundary, ApiError, StaleSessionError } from '@/api/http'
import type { AuthSession } from '@/api/types'
import { queryClient } from '@/queryClient'
import { createUuid } from '@/utils/uuid'
import { purgePortalChatDrafts } from '@/composables/usePortalChatDrafts'
import { acceptPortalOnlineSession, portalOfflineState, purgePortalOffline, readPortalOfflineSnapshot, restorePortalOfflineSession, suspendPortalOfflineSession } from '@/portal/portalOffline'
import { acceptPortalPreviewSession, closePortalPreview, endPortalPreview, isPortalPreviewActive, PortalPreviewReadonlyError, portalPreviewState } from '@/portal/portalPreview'

const AUTH_BOUNDARY_KEY = 'avoschool.auth-boundary'
const AUTH_VERIFICATION_KEY = 'avoschool.auth-needs-verification'
function verificationMarker(): string | null { try { return sessionStorage.getItem(AUTH_VERIFICATION_KEY) } catch { return null } }
function markVerification(value: string | null): void { try { if (value) sessionStorage.setItem(AUTH_VERIFICATION_KEY, value); else sessionStorage.removeItem(AUTH_VERIFICATION_KEY) } catch { /* Storage can be disabled. */ } }

export const useAuthStore = defineStore('auth', () => {
  const session = ref<AuthSession | null>(null)
  const initialized = ref(false)
  const pending = ref(false)
  let initializeRequest: Promise<AuthSession | null> | null = null
  let previousAccountId: string | null = null

  const authenticated = computed(() => session.value !== null)

  function hasPermission(permission?: string | null): boolean {
    return !permission
      || session.value?.roles.includes('owner') === true
      || session.value?.permissions.includes(permission) === true
  }

  function hasAnyPermission(permissions: readonly string[]): boolean {
    return permissions.some(hasPermission)
  }

  function sessionKey(value: AuthSession | null): string | null {
    return value?.accountId ?? null
  }

  function replaceSession(value: AuthSession | null, forceBoundary = false): void {
    if (forceBoundary || sessionKey(session.value) !== sessionKey(value)) {
      queryClient.clear()
      advanceApiSessionBoundary()
    }
    session.value = value
    if (value) previousAccountId = value.accountId
    if (!isPortalPreviewActive()) window.dispatchEvent(new CustomEvent('avoschool:session-changed', { detail: value }))
  }

  async function initialize(force = false): Promise<AuthSession | null> {
    if (isPortalPreviewActive()) {
      if (portalPreviewState.phase.value !== 'ready') { initialized.value = true; return null }
      if (!force && initialized.value && session.value?.portalPreview) return session.value
      if (initializeRequest) return initializeRequest
      initializeRequest = authApi.me().then(value => {
        acceptPortalPreviewSession(value)
        replaceSession(value)
        initialized.value = true
        return value
      }).catch(error => {
        endPortalPreview(error instanceof Error ? error.message : undefined)
        replaceSession(null, true)
        initialized.value = true
        return null
      }).finally(() => { initializeRequest = null })
      return initializeRequest
    }
    if (verificationMarker() === 'signed-out') { initialized.value = true; return null }
    if (!force && initialized.value && (!portalOfflineState.verificationRequired.value || !navigator.onLine)) return session.value
    if (initializeRequest) return initializeRequest

    initializeRequest = authApi.me()
      .then(value => {
        switchAccount(value)
        replaceSession(value)
        markVerification(null)
        acceptPortalOnlineSession(value)
        initialized.value = true
        return value
      })
      .catch(error => {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          purgePortalOffline()
          replaceSession(null, true)
          initialized.value = true
          if (error.status === 403) throw error
          return null
        }
        const networkFailure = !(error instanceof ApiError) && !(error instanceof StaleSessionError)
          && error?.name !== 'AbortError' && (error instanceof TypeError || !navigator.onLine)
        const cached = portalOfflineState.enabled.value && networkFailure && !verificationMarker() ? readPortalOfflineSnapshot() : null
        if (cached) {
          replaceSession(cached.session, true)
          if (restorePortalOfflineSession()) {
            initialized.value = true
            return cached.session
          }
          replaceSession(null, true)
        }
        if (networkFailure && !navigator.onLine) {
          replaceSession(null, true)
          initialized.value = true
          return null
        }
        initialized.value = false
        throw error
      })
      .finally(() => {
        initializeRequest = null
      })
    return initializeRequest
  }

  function broadcastSession(kind: 'login' | 'logout', accountId: string | null): void {
    markVerification(kind === 'logout' ? 'signed-out' : null)
    try { localStorage.setItem(AUTH_BOUNDARY_KEY, JSON.stringify({ kind, accountId, nonce: createUuid() })) } catch { /* Other tabs will revalidate when they reconnect. */ }
  }

  function onExternalSession(event: StorageEvent): void {
    if (isPortalPreviewActive()) return
    if (event.key !== AUTH_BOUNDARY_KEY || !event.newValue) return
    let change: { kind?: string; accountId?: string | null }
    try { change = JSON.parse(event.newValue) } catch { return }
    if (!['login', 'logout'].includes(change.kind ?? '')) return
    const previous = session.value?.accountId ?? previousAccountId
    if (previous && (change.kind === 'logout' || previous !== change.accountId)) {
      purgePortalOffline(previous)
      void purgePortalChatDrafts(previous)
    }
    // Never let the old tab republish its account or render private observers while another tab changes the cookie.
    markVerification(change.kind === 'logout' ? 'signed-out' : 'required')
    suspendPortalOfflineSession()
    replaceSession(null, true)
    initialized.value = false
    // Remount query observers as well as the session. The per-tab marker forbids cache restoration before /auth/me.
    window.location.reload()
  }
  window.addEventListener('storage', onExternalSession)
  onScopeDispose(() => window.removeEventListener('storage', onExternalSession))

  function switchAccount(value: AuthSession): void {
    const previous = session.value?.accountId ?? previousAccountId ?? readPortalOfflineSnapshot()?.session.accountId
    if (previous && previous !== value.accountId) {
      purgePortalOffline(previous)
      void purgePortalChatDrafts(previous)
    }
  }

  function forgetAccount(): void {
    const accountId = session.value?.accountId ?? previousAccountId ?? readPortalOfflineSnapshot()?.session.accountId
    purgePortalOffline()
    if (accountId) void purgePortalChatDrafts(accountId)
    previousAccountId = null
  }

  async function login(email: string, password: string): Promise<AuthSession> {
    if (isPortalPreviewActive()) throw new PortalPreviewReadonlyError()
    pending.value = true
    try {
      const value = await authApi.login(email, password)
      // Login is always a trust boundary: the previous browser user may have
      // left cached private data even when the local session already looks empty.
      switchAccount(value)
      replaceSession(value, true)
      markVerification(null)
      acceptPortalOnlineSession(value)
      broadcastSession('login', value.accountId)
      initialized.value = true
      return value
    } finally {
      pending.value = false
    }
  }

  async function activateInvite(token: string, username: string, password: string): Promise<AuthSession> {
    if (isPortalPreviewActive()) throw new PortalPreviewReadonlyError()
    pending.value = true
    try {
      const value = await authApi.activateInvite(token, { username, password })
      switchAccount(value)
      replaceSession(value, true)
      markVerification(null)
      acceptPortalOnlineSession(value)
      broadcastSession('login', value.accountId)
      initialized.value = true
      return value
    } finally {
      pending.value = false
    }
  }

  async function logout(): Promise<void> {
    if (isPortalPreviewActive()) {
      pending.value = true
      try { await closePortalPreview() }
      catch { /* This tab stays closed even when server revocation could not be reached; the grant expires. */ }
      finally { replaceSession(null, true); initialized.value = true; pending.value = false }
      return
    }
    window.dispatchEvent(new CustomEvent('avoschool:explicit-logout'))
    window.dispatchEvent(new CustomEvent('avoschool:session-changed', { detail: null }))
    forgetAccount()
    broadcastSession('logout', null)
    replaceSession(null, true)
    pending.value = true
    try {
      await authApi.logout()
    } finally {
      replaceSession(null, true)
      initialized.value = true
      pending.value = false
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (isPortalPreviewActive()) throw new PortalPreviewReadonlyError()
    pending.value = true
    try {
      await authApi.changePassword({ currentPassword, newPassword })
      forgetAccount()
      broadcastSession('logout', null)
      replaceSession(null, true)
      initialized.value = true
    } finally {
      pending.value = false
    }
  }

  function clear(): void {
    if (isPortalPreviewActive()) {
      if (portalPreviewState.phase.value !== 'ended') endPortalPreview()
      replaceSession(null, true)
      initialized.value = true
      return
    }
    // Expiry clears cached private data, but keeps the same account’s unsent draft for reauthentication.
    purgePortalOffline()
    replaceSession(null, true)
    initialized.value = true
  }

  function setHiddenNavigationItemIds(itemIds: readonly string[]): void {
    if (!session.value) return
    session.value = {
      ...session.value,
      hiddenNavigationItemIds: [...itemIds],
    }
  }

  return {
    authenticated,
    initialized,
    pending,
    session,
    clear,
    activateInvite,
    changePassword,
    hasAnyPermission,
    hasPermission,
    initialize,
    login,
    logout,
    setHiddenNavigationItemIds,
  }
})
